import type { Photo } from './photo'
import type { WorkspacePOI } from './poi'
import type { OmitUnderscoredAndNoneFlatProperties } from './syncableEntity'
import type { RoutePoint, Segment, Tour, Track, Waypoint } from './tour'
import type { Workspace } from './workspace'

// Event types that represent all possible operations
export enum EventType {
  // Workspace Events
  WORKSPACE_CREATED = 'WORKSPACE_CREATED',
  WORKSPACE_UPDATED = 'WORKSPACE_UPDATED',
  WORKSPACE_DELETED = 'WORKSPACE_DELETED',
  WORKSPACE_POI_CREATED = 'WORKSPACE_POI_CREATED',
  WORKSPACE_POI_UPDATED = 'WORKSPACE_POI_UPDATED',
  WORKSPACE_POI_DELETED = 'WORKSPACE_POI_DELETED',
  WORKSPACE_POI_ADDED_TO_WORKSPACE = 'WORKSPACE_POI_ADDED_TO_WORKSPACE',
  WORKSPACE_POI_REMOVED_FROM_WORKSPACE = 'WORKSPACE_POI_REMOVED_FROM_WORKSPACE',

  // Tour Events
  TOUR_CREATED = 'TOUR_CREATED',
  TOUR_ADDED_TO_WORKSPACE = 'TOUR_ADDED_TO_WORKSPACE',
  TOUR_MEMBERSHIP_UPDATED = 'TOUR_MEMBERSHIP_UPDATED',
  TOUR_UPDATED = 'TOUR_UPDATED',
  TOUR_DELETED = 'TOUR_DELETED',
  TOUR_REMOVED_FROM_WORKSPACE = 'TOUR_REMOVED_FROM_WORKSPACE',

  // Track Events
  TRACK_CREATED = 'TRACK_CREATED',
  TRACK_UPDATED = 'TRACK_UPDATED',
  TRACK_DELETED = 'TRACK_DELETED',

  // Segment Events
  SEGMENT_CREATED = 'SEGMENT_CREATED',
  SEGMENT_UPDATED = 'SEGMENT_UPDATED',
  SEGMENT_DELETED = 'SEGMENT_DELETED',

  // Waypoint Events
  WAYPOINT_CREATED = 'WAYPOINT_CREATED',
  WAYPOINT_UPDATED = 'WAYPOINT_UPDATED',
  WAYPOINT_DELETED = 'WAYPOINT_DELETED',

  // Route Point Events
  ROUTE_POINT_CREATED = 'ROUTE_POINT_CREATED',
  ROUTE_POINT_UPDATED = 'ROUTE_POINT_UPDATED',
  ROUTE_POINT_DELETED = 'ROUTE_POINT_DELETED',

  // Photo Events
  PHOTO_CREATED = 'PHOTO_CREATED',
  PHOTO_UPDATED = 'PHOTO_UPDATED',
  PHOTO_DELETED = 'PHOTO_DELETED',

  // Workspace cover photo
  WORKSPACE_COVER_PHOTO_SET = 'WORKSPACE_COVER_PHOTO_SET',
}

export interface BaseEvent {
  clientId: string
  type: EventType
  timestamp: number
  _sizeBytes?: number
  _queueOrder?: number
  // Quarantine (docs/concepts/sync-feedback.md §6): server-rejected events are moved aside so
  // the rest of the queue keeps syncing. `_`-prefixed = local-only, never sent to the server.
  _quarantined?: boolean
  _quarantineReason?: string
}

export interface EventChange {
  field: string
  value?: string | number | boolean | File | null
}

export type EventChanges = EventChange[] | Record<string, unknown>

// Workspace events
export interface WorkspaceCreatedEvent extends BaseEvent {
  type: EventType.WORKSPACE_CREATED
  payload: OmitUnderscoredAndNoneFlatProperties<Workspace>
}

export interface WorkspaceUpdatedEvent extends BaseEvent {
  type: EventType.WORKSPACE_UPDATED
  payload: {
    clientId: string
    changes: EventChanges
  }
}

export interface WorkspaceDeletedEvent extends BaseEvent {
  type: EventType.WORKSPACE_DELETED
  payload: {
    clientId: string
  }
}

export interface WorkspacePOICreatedEvent extends BaseEvent {
  type: EventType.WORKSPACE_POI_CREATED
  payload: OmitUnderscoredAndNoneFlatProperties<WorkspacePOI>
}

export interface WorkspacePOIUpdatedEvent extends BaseEvent {
  type: EventType.WORKSPACE_POI_UPDATED
  payload: {
    clientId: string
    changes: EventChanges
  }
}

export interface WorkspacePOIDeletedEvent extends BaseEvent {
  type: EventType.WORKSPACE_POI_DELETED
  payload: {
    clientId: string
  }
}

export interface WorkspacePOIAddedToWorkspaceEvent extends BaseEvent {
  type: EventType.WORKSPACE_POI_ADDED_TO_WORKSPACE
  payload: {
    clientId: string
    workspaceClientId: string
  }
}

export interface WorkspacePOIRemovedFromWorkspaceEvent extends BaseEvent {
  type: EventType.WORKSPACE_POI_REMOVED_FROM_WORKSPACE
  payload: {
    clientId: string
    workspaceClientId: string
  }
}

// Tour events
export interface TourCreatedEvent extends BaseEvent {
  type: EventType.TOUR_CREATED
  payload: { workspaceClientId: string | null } & OmitUnderscoredAndNoneFlatProperties<Tour>
}

export interface TourAddedToWorkspaceEvent extends BaseEvent {
  type: EventType.TOUR_ADDED_TO_WORKSPACE
  payload: {
    client_id: string
    workspace_client_id: string
  }
}

export interface TourUpdatedEvent extends BaseEvent {
  type: EventType.TOUR_UPDATED
  payload: {
    clientId: string
    changes: EventChanges
  }
}

export interface TourMembershipUpdatedEvent extends BaseEvent {
  type: EventType.TOUR_MEMBERSHIP_UPDATED
  payload: {
    client_id: string
    workspace_client_id: string
    changes: EventChanges
  }
}

export interface TourDeletedEvent extends BaseEvent {
  type: EventType.TOUR_DELETED
  payload: {
    clientId: string
  }
}

export interface TourRemovedFromWorkspaceEvent extends BaseEvent {
  type: EventType.TOUR_REMOVED_FROM_WORKSPACE
  payload: {
    client_id: string
    workspace_client_id: string
  }
}

// Track events
export interface TrackCreatedEvent extends BaseEvent {
  type: EventType.TRACK_CREATED
  payload: { tour_client_id: string } & OmitUnderscoredAndNoneFlatProperties<Track>
}

export interface TrackUpdatedEvent extends BaseEvent {
  type: EventType.TRACK_UPDATED
  payload: {
    clientId: string
    changes: EventChanges
  }
}

export interface TrackDeletedEvent extends BaseEvent {
  type: EventType.TRACK_DELETED
  payload: {
    clientId: string
  }
}

// Segment events
export interface SegmentCreatedEvent extends BaseEvent {
  type: EventType.SEGMENT_CREATED
  payload: { trackClientId: string; order: number } & Segment
}

export interface SegmentUpdatedEvent extends BaseEvent {
  type: EventType.SEGMENT_UPDATED
  payload: {
    clientId: string
    trackClientId: string
    order: number
    points?: Segment['points']
  } & Partial<Omit<Segment, 'clientId' | 'points'>>
}

export interface SegmentDeletedEvent extends BaseEvent {
  type: EventType.SEGMENT_DELETED
  payload: {
    clientId: string
  }
}

// Waypoint events
export interface WaypointCreatedEvent extends BaseEvent {
  type: EventType.WAYPOINT_CREATED
  payload: { tourClientId: string } & OmitUnderscoredAndNoneFlatProperties<Waypoint>
}

export interface WaypointUpdatedEvent extends BaseEvent {
  type: EventType.WAYPOINT_UPDATED
  payload: {
    clientId: string
    changes: EventChanges
  }
}

export interface WaypointDeletedEvent extends BaseEvent {
  type: EventType.WAYPOINT_DELETED
  payload: {
    clientId: string
  }
}

// Route point events
export interface RoutePointCreatedEvent extends BaseEvent {
  type: EventType.ROUTE_POINT_CREATED
  payload: OmitUnderscoredAndNoneFlatProperties<RoutePoint>
}

export interface RoutePointUpdatedEvent extends BaseEvent {
  type: EventType.ROUTE_POINT_UPDATED
  payload: {
    clientId: string
    changes: EventChanges
  }
}

export interface RoutePointDeletedEvent extends BaseEvent {
  type: EventType.ROUTE_POINT_DELETED
  payload: {
    clientId: string
  }
}

// Photo events
export interface PhotoCreatedEvent extends BaseEvent {
  type: EventType.PHOTO_CREATED
  payload: { workspaceClientId: string } & OmitUnderscoredAndNoneFlatProperties<Photo>
}

export interface PhotoUpdatedEvent extends BaseEvent {
  type: EventType.PHOTO_UPDATED
  payload: {
    clientId: string
    changes: EventChanges
  }
}

export interface PhotoDeletedEvent extends BaseEvent {
  type: EventType.PHOTO_DELETED
  payload: {
    clientId: string
  }
}

export interface WorkspaceCoverPhotoSetEvent extends BaseEvent {
  type: EventType.WORKSPACE_COVER_PHOTO_SET
  payload: {
    clientId: string // workspace client ID
    coverPhotoClientId: string | null
  }
}

// Union type for all events
export type Event =
  | WorkspaceCreatedEvent
  | WorkspaceUpdatedEvent
  | WorkspaceDeletedEvent
  | WorkspacePOICreatedEvent
  | WorkspacePOIUpdatedEvent
  | WorkspacePOIDeletedEvent
  | WorkspacePOIAddedToWorkspaceEvent
  | WorkspacePOIRemovedFromWorkspaceEvent
  | TourCreatedEvent
  | TourAddedToWorkspaceEvent
  | TourMembershipUpdatedEvent
  | TourUpdatedEvent
  | TourDeletedEvent
  | TourRemovedFromWorkspaceEvent
  | TrackCreatedEvent
  | TrackUpdatedEvent
  | TrackDeletedEvent
  | SegmentCreatedEvent
  | SegmentUpdatedEvent
  | SegmentDeletedEvent
  | WaypointCreatedEvent
  | WaypointUpdatedEvent
  | WaypointDeletedEvent
  | RoutePointCreatedEvent
  | RoutePointUpdatedEvent
  | RoutePointDeletedEvent
  | PhotoCreatedEvent
  | PhotoUpdatedEvent
  | PhotoDeletedEvent
  | WorkspaceCoverPhotoSetEvent
